import * as React from "react";
import {CensusCoolerContext} from "../../providers/CensusCollerProvider";
import {PageOfCensusEquipmentParam} from "../../data/controllers/CensusCoolerController";
import {useCensusEntriesByInventoryStatus} from "../../data/hooks/CecnsusCoolerControllerHooks";
import {
    Actions,
    ActionsEnum,
    CensusEquipmentStatusDetailsEnum,
    CensusEquipmentStatusesEnum,
    CensusEquipmentStatusReasons,
} from "../../data/models/CensusEnums";
import {EmptyState, infoIcons} from "@vebox/etui-core";
import {createColumnsList, createDatasource} from "./utils";
import "./styles.scss";
import {InfiniteScrollTable} from "../../components/InfiniteScrollTable";
import {
    JSONCensusEquipmentsActionsConfirmation,
    ManualChange,
} from "../../data/models/JSONCensusEquipmentsActionsConfirmation";

interface SuggestedActionsTableProps {
    status: CensusEquipmentStatusesEnum;
    statusDetail: CensusEquipmentStatusDetailsEnum;
    availableActions: ActionsEnum[],
    statusReason?: CensusEquipmentStatusReasons;
    translatedActionsEnum: Actions;
    confirmedAction: JSONCensusEquipmentsActionsConfirmation;
    isMixedActionSelected: boolean;
    addOrRemoveAndMergeManualChange: (
        action: JSONCensusEquipmentsActionsConfirmation,
        manualChange: ManualChange,
    ) => void;
    height: string;
}

export const SuggestedActionsTable: React.FC<SuggestedActionsTableProps> = ({
                                                                                statusDetail,
                                                                                status,
                                                                                translatedActionsEnum,
                                                                                availableActions,
                                                                                statusReason,
                                                                                addOrRemoveAndMergeManualChange,
                                                                                confirmedAction,
                                                                                isMixedActionSelected,
                                                                                height,
                                                                            }) => {
    const [currentPage, setCurrentPage] = React.useState(0);

    const {census, AppTranslations, getEquipmentTableHeadersName} =
        React.useContext(CensusCoolerContext);
    const params: PageOfCensusEquipmentParam = React.useMemo(() => {
        const reason = statusReason === CensusEquipmentStatusReasons.OTHER ? null : statusReason;
        return {
            guid: census?.guid,
            page: 0,
            size: 20,
            status: status,
            statusDetail: statusDetail,
            statusReason: reason,
        };
    }, [census?.guid, status, statusDetail]);
    const {
        flattenedData,
        hasNextPage,
        isNextPageLoading,
        handleOnLoadNextPage,
        isFetching,
    } = useCensusEntriesByInventoryStatus(params);

    const onLoadNextPage = (): Promise<void> => {
        const newPage = currentPage + 1;
        setCurrentPage(newPage);
        return handleOnLoadNextPage(newPage);
    };

    const list = React.useMemo(() => {
        return createDatasource(
            flattenedData ?? [],
            translatedActionsEnum,
            confirmedAction,
            addOrRemoveAndMergeManualChange,
            isMixedActionSelected,
            availableActions
        );
    }, [
        flattenedData,
        isNextPageLoading,
        confirmedAction?.action,
        confirmedAction?.manualChanges,
        translatedActionsEnum,
        isMixedActionSelected,
        availableActions
    ]);

    return (
        <div className={"suggested-action-table-container"}>
            <InfiniteScrollTable
                data={list}
                columns={createColumnsList(getEquipmentTableHeadersName)}
                update={onLoadNextPage}
                hasNextPage={hasNextPage}
                isLoading={isFetching}
                height={height}
                emptyPlaceholder={
                    <EmptyState>
                        <EmptyState.InfoIcon
                            styles={{
                                root: {
                                    color: "var(--empty-indicator-svg-stroke)",
                                },
                            }}
                            glyph={infoIcons.NoContent}
                        />
                        <EmptyState.Title color="var(--empty-indicator-title-color)">
                            {AppTranslations.noDataAvailable}
                        </EmptyState.Title>
                    </EmptyState>
                }
            />
        </div>
    );
};
